import { addDays, differenceInCalendarDays } from "date-fns"
import { parseDateOnly } from "@/lib/datetime"
import type { TcmEhrStatus, TcmEpisode, TcmStageCode, TcmSummary, TcmUrgency } from "../types"

/** The all-clinics view: a superuser in one of Pear's own organizations. Every other org,
 * even one with no TCM cases or before the summary loads, shows only its own cases. */
export function isPearView(isSuperuser: boolean, orgId: string | null, summary: TcmSummary | undefined): boolean {
  return isSuperuser && !!orgId && (summary?.internal_organization_ids ?? []).includes(orgId)
}

export const STAGE_GROUPS: { code: TcmStageCode; title: string; hint: string }[] = [
  { code: "CONTACT_OVERDUE", title: "2-day contact missed", hint: "Contact or 2 documented attempts was due within 2 business days of discharge." },
  { code: "CONTACT_DUE", title: "Call within 2 business days", hint: "Phone, portal or in person, or 2 documented attempts." },
  { code: "VISIT_OVERDUE", title: "Visit date passed", hint: "Mark the visit done or rebook it." },
  { code: "BOOK_VISIT", title: "Book the face-to-face visit", hint: "By day 7 keeps 99496 open; by day 14 for 99495." },
  { code: "VISIT_UPCOMING", title: "Visit booked", hint: "Paste the note into the visit before the patient arrives." },
  { code: "READY_TO_BILL", title: "Ready to bill", hint: "Visit done. Bill TCM once the 2-day contact is logged; without it, bill an office visit." },
  { code: "RECORDS_NEEDED", title: "Get the hospital records", hint: "Hospital stay or ED visit, and the discharge date, aren't confirmed yet. No billing clock until they are." },
  { code: "IN_HOSPITAL", title: "Still in the hospital", hint: "The clock starts at discharge." },
]

export const DONE_GROUPS: { code: TcmStageCode; title: string; hint: string }[] = [
  { code: "BILLED", title: "Billed", hint: "Last 90 days." },
  { code: "FOLLOWED_UP", title: "ED follow-up done", hint: "" },
  { code: "CLOSED", title: "Closed without billing", hint: "Readmitted, seen elsewhere, declined or missed." },
]

export type TileId = "contact" | "book" | "week" | "bill" | "hospital"

export const TILES: { id: TileId; label: string; sub: string }[] = [
  { id: "contact", label: "Calls due", sub: "2-business-day contact" },
  { id: "book", label: "Visits to book", sub: "No face-to-face on the calendar" },
  { id: "week", label: "Visits this week", sub: "Paste the note before the visit" },
  { id: "bill", label: "Ready to bill", sub: "Claim can go in now" },
  { id: "hospital", label: "In the hospital", sub: "Clock starts at discharge" },
]

const URGENCY_ORDER: Record<TcmUrgency, number> = { red: 0, amber: 1, none: 2, done: 3 }

export function visitDate(ep: TcmEpisode): Date | null {
  return parseDateOnly(ep.f2f_completed_date ?? ep.f2f_scheduled_date)
}

function visitThisWeek(ep: TcmEpisode, today: Date): boolean {
  const visit = parseDateOnly(ep.f2f_scheduled_date)
  if (ep.stage.code !== "VISIT_UPCOMING" || !visit) return false
  const days = differenceInCalendarDays(visit, today)
  return days >= 0 && days <= 7
}

export function matchesTile(ep: TcmEpisode, tile: TileId, today: Date): boolean {
  const code = ep.stage.code
  switch (tile) {
    case "contact":
      return code === "CONTACT_OVERDUE" || code === "CONTACT_DUE"
    case "book":
      return code === "BOOK_VISIT" || code === "VISIT_OVERDUE"
    case "week":
      return visitThisWeek(ep, today)
    case "bill":
      return code === "READY_TO_BILL"
    case "hospital":
      return code === "IN_HOSPITAL"
  }
}

export function tileCounts(episodes: TcmEpisode[], today: Date) {
  return Object.fromEntries(
    TILES.map((t) => {
      const hits = episodes.filter((ep) => matchesTile(ep, t.id, today))
      return [t.id, {
        count: hits.length,
        urgent: hits.some((ep) => ep.stage.urgency === "red"),
        notesReady: hits.filter((ep) => ep.note).length,
      }]
    }),
  ) as Record<TileId, { count: number; urgent: boolean; notesReady: number }>
}

function byUrgencyThenDay(a: TcmEpisode, b: TcmEpisode): number {
  return (
    URGENCY_ORDER[a.stage.urgency] - URGENCY_ORDER[b.stage.urgency] ||
    (b.clock?.day ?? 0) - (a.clock?.day ?? 0) ||
    a.patient_name.localeCompare(b.patient_name)
  )
}

export interface CaseGroup {
  key: string
  title: string
  hint: string
  urgent: boolean
  episodes: TcmEpisode[]
}

export function groupByStage(
  episodes: TcmEpisode[],
  groups: { code: TcmStageCode; title: string; hint: string }[],
): CaseGroup[] {
  return groups
    .map((g) => {
      const rows = episodes.filter((ep) => ep.stage.code === g.code).sort(byUrgencyThenDay)
      return { key: g.code, title: g.title, hint: g.hint, urgent: rows.some((r) => r.stage.urgency === "red"), episodes: rows }
    })
    .filter((g) => g.episodes.length > 0)
}

/** Booked visits in the next `days` days, one group per day, earliest first. */
export function groupByVisitDay(episodes: TcmEpisode[], today: Date, days = 14): CaseGroup[] {
  const horizon = addDays(today, days)
  const byDay = new Map<string, TcmEpisode[]>()
  for (const ep of episodes) {
    const visit = parseDateOnly(ep.f2f_scheduled_date)
    if (ep.f2f_completed_date || !visit || visit < today || visit > horizon) continue
    const key = ep.f2f_scheduled_date as string
    byDay.set(key, [...(byDay.get(key) ?? []), ep])
  }
  return Array.from(byDay.keys())
    .sort()
    .map((key) => {
      const rows = byDay.get(key) as TcmEpisode[]
      const missing = rows.filter((ep) => !ep.note).length
      return {
        key,
        title: dayTitle(parseDateOnly(key) as Date, today),
        hint: missing ? `${missing} note${missing > 1 ? "s" : ""} not ready yet` : "All notes ready to paste",
        urgent: false,
        episodes: rows.sort((a, b) => a.patient_name.localeCompare(b.patient_name)),
      }
    })
}

function dayTitle(d: Date, today: Date): string {
  const label = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
  const diff = differenceInCalendarDays(d, today)
  return diff === 0 ? `${label} (today)` : diff === 1 ? `${label} (tomorrow)` : label
}

export type BadgeVariant = "success" | "warning" | "destructive" | "info" | "muted"

export function urgencyVariant(urgency: TcmUrgency): BadgeVariant {
  return urgency === "red" ? "destructive" : urgency === "amber" ? "warning" : urgency === "done" ? "success" : "muted"
}

export function urgencyText(urgency: TcmUrgency): string {
  return urgency === "red" ? "text-destructive" : urgency === "amber" ? "text-warning" : urgency === "done" ? "text-success" : "text-foreground"
}

/** The latest care plan's readmission risk, when there is one (never for ED follow-ups). */
export function riskBadge(ep: TcmEpisode): { label: string; variant: BadgeVariant } | null {
  const tier = ep.risk_tier
  if (!tier || tier === "NA") return null
  const at = ep.risk_tier_complete === false && tier !== "HIGH" ? "≥ " : ""
  if (tier === "HIGH") return { label: `${at}High readmission risk`, variant: "destructive" }
  if (tier === "INTERMEDIATE") return { label: `${at}Medium readmission risk`, variant: "warning" }
  return { label: `${at}Low readmission risk`, variant: "muted" }
}

export function noteBadge(ep: TcmEpisode, ehr: string): { label: string; variant: BadgeVariant } {
  const status: TcmEhrStatus = ep.ehr_status
  if (status === "filed") return { label: `In ${ehr}`, variant: "success" }
  if (status === "stale") return { label: `Newer note than ${ehr}`, variant: "warning" }
  if (status === "not_filed") return { label: `Not in ${ehr} yet`, variant: "warning" }
  return { label: ep.stage.code === "IN_HOSPITAL" ? "No note yet" : "Note pending", variant: "muted" }
}

const LEVELS: Record<string, string> = {
  INPATIENT: "Inpatient",
  OBSERVATION: "Observation",
  HOSPITAL: "Hospital stay",
  SNF: "Skilled nursing",
  IRF: "Inpatient rehab",
  LTCH: "Long-term acute care",
  PARTIAL_HOSPITALIZATION: "Partial hospitalization",
  ED: "Emergency department",
}

export function levelLabel(level: string): string {
  return LEVELS[level] ?? level
}

export const CLOSE_REASONS: { value: string; label: string }[] = [
  { value: "READMITTED", label: "Readmitted" },
  { value: "SEEN_ELSEWHERE", label: "Seen elsewhere" },
  { value: "DECLINED", label: "Patient declined" },
  { value: "DECEASED", label: "Deceased" },
  { value: "MISSED_WINDOW", label: "Missed the window" },
  { value: "DUPLICATE", label: "Duplicate" },
  { value: "OTHER", label: "Other" },
]

export function closeReasonLabel(reason: string | null): string {
  return CLOSE_REASONS.find((r) => r.value === reason)?.label ?? reason ?? ""
}

/** Clock-bar positions, as a percent of the 30-day period. */
export function clockPercent(day: number | null | undefined): number {
  if (day === null || day === undefined) return 0
  return Math.max(0, Math.min(100, (day / 30) * 100))
}
